import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Plus, Search, X } from "lucide-react";

const TAGS = ["Hypertrophy", "Performance", "Rehab", "Strength", "Mobility", "general"];

function ClientFormModal({ open, onClose, onSaved, initial }) {
    const [form, setForm] = useState(
        initial || { name: "", email: "", phone: "", goal: "", tag: "general", status: "active", avatar_url: "", experience: "beginner" }
    );
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    if (!open) return null;
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            if (initial?.id) {
                const { data } = await api.put(`/clients/${initial.id}`, form);
                onSaved(data);
            } else {
                const { data } = await api.post("/clients", form);
                onSaved(data);
            }
            onClose();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="client-form-modal">
            <div className="bg-[var(--surface)] border border-[var(--border-bright)] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                    <h3 className="font-display text-2xl uppercase">{initial?.id ? "Edit Client" : "New Client"}</h3>
                    <button onClick={onClose} data-testid="close-client-modal"><X size={18} /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <div>
                        <label className="label-tiny block mb-2">Full name</label>
                        <input data-testid="client-name-input" required className="input-dark" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-tiny block mb-2">Email</label>
                            <input className="input-dark" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Phone</label>
                            <input className="input-dark" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        </div>
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Goal</label>
                        <input className="input-dark" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-tiny block mb-2">Tag</label>
                            <select className="input-dark" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })}>
                                {TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Status</label>
                            <select className="input-dark" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                                <option value="active">Active</option>
                                <option value="paused">Paused</option>
                                <option value="archived">Archived</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Fitness level <span className="text-[var(--gold-bright)] font-mono normal-case tracking-normal">— auto-schedules the 16-week bodyweight program</span></label>
                        <select
                            className="input-dark"
                            value={form.experience || "beginner"}
                            onChange={(e) => setForm({ ...form, experience: e.target.value })}
                            data-testid="client-experience-select"
                        >
                            <option value="beginner">Beginner — brand new to training</option>
                            <option value="intermediate">Intermediate — 6+ months consistent</option>
                            <option value="advanced">Advanced — 2+ years, strong base</option>
                        </select>
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Avatar URL (optional)</label>
                        <input className="input-dark" value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} />
                    </div>
                    {err && <div className="text-sm text-[var(--danger)]">{err}</div>}
                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn-primary" disabled={busy} data-testid="save-client-btn">
                            {busy ? "Saving…" : initial?.id ? "Save Changes" : "Create Client"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function ClientsPage() {
    const [clients, setClients] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        const { data } = await api.get("/clients");
        setClients(data);
    };
    useEffect(() => { load(); }, []);

    const filtered = clients.filter(
        (c) => c.name.toLowerCase().includes(q.toLowerCase()) || (c.tag || "").toLowerCase().includes(q.toLowerCase())
    );

    return (
        <div className="p-8 space-y-6" data-testid="clients-page">
            <div className="flex items-end justify-between">
                <div>
                    <p className="label-tiny mb-2">/ Roster</p>
                    <h1 className="font-serif text-6xl">Clients</h1>
                </div>
                <button className="btn-primary inline-flex items-center gap-2" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-client-btn">
                    <Plus size={16} /> New Client
                </button>
            </div>

            <div className="flex items-center gap-3 border border-[var(--border)] bg-[var(--surface)] px-4 py-2">
                <Search size={14} className="text-[var(--text-muted)]" />
                <input
                    data-testid="clients-search"
                    placeholder="SEARCH NAME OR TAG"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="bg-transparent outline-none flex-1 text-sm tracking-wider uppercase"
                />
                <span className="font-mono text-xs text-[var(--text-muted)]">{filtered.length} / {clients.length}</span>
            </div>

            <div className="border border-[var(--border)] bg-[var(--surface)]">
                <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[var(--border)] label-tiny">
                    <div className="col-span-4">Client</div>
                    <div className="col-span-2">Tag</div>
                    <div className="col-span-3">Goal</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-1 text-right">—</div>
                </div>
                {filtered.length === 0 && (
                    <div className="p-12 text-center text-[var(--text-muted)]">No clients match.</div>
                )}
                {filtered.map((c) => (
                    <Link
                        key={c.id}
                        to={`/clients/${c.id}`}
                        data-testid={`client-row-${c.id}`}
                        className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-[var(--border)] hover:bg-[var(--surface-hover)] items-center"
                    >
                        <div className="col-span-4 flex items-center gap-3">
                            {c.avatar_url ? (
                                <img src={c.avatar_url} alt="" className="w-9 h-9 object-cover" />
                            ) : (
                                <div className="w-9 h-9 bg-[var(--surface-hover)] flex items-center justify-center font-display text-sm">
                                    {c.name.charAt(0)}
                                </div>
                            )}
                            <div>
                                <p className="font-medium">{c.name}</p>
                                <p className="text-xs text-[var(--text-muted)] font-mono">{c.email || "—"}</p>
                            </div>
                        </div>
                        <div className="col-span-2"><span className="tag-pill">{c.tag}</span></div>
                        <div className="col-span-3 text-sm text-[var(--text-muted)] truncate">{c.goal || "—"}</div>
                        <div className="col-span-2">
                            <span className={`tag-pill ${c.status === "active" ? "border-[var(--success)] text-[var(--success)]" : ""}`}>
                                {c.status}
                            </span>
                        </div>
                        <div className="col-span-1 text-right text-[var(--primary)] font-mono">→</div>
                    </Link>
                ))}
            </div>

            <ClientFormModal open={open} onClose={() => setOpen(false)} initial={editing} onSaved={load} />
        </div>
    );
}
